
import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { CartItem, Coupon, Order } from '../types';
import { useStore } from './StoreContext';
import { supabase } from '../services/supabaseClient';

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (internalId: string) => void;
  updateQuantity: (internalId: string, delta: number) => void;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
  placeOrder: (customer: any, tip?: number) => Promise<string | { checkoutUrl: string }>;
  activeCoupon: Coupon | null;
  discountAmount: number;
  isProcessing: boolean;
  loyaltyReward: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { restaurant } = useStore();
  const [items, setItems] = useState<CartItem[]>([]);
  const [activeCoupon, setActiveCoupon] = useState<Coupon | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const cartExpiryRef = useRef<number>(Date.now());

  const loyaltyReward = 0.25;

  const subTotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const discountAmount = activeCoupon ? (subTotal * activeCoupon.discountPercentage) / 100 : 0;
  const cartTotal = Math.max(0, subTotal - discountAmount);

  const placeOrder = async (customer: any, tip: number = 0): Promise<string | { checkoutUrl: string }> => {
    if (isProcessing) throw new Error("Transação em andamento...");
    if (!restaurant?.isOpen) throw new Error("Estabelecimento fechado.");
    if (items.length === 0) throw new Error("Sacola vazia.");

    setIsProcessing(true);
    try {
      const deliveryFee = (restaurant.delivery.minOrderForFreeDelivery && subTotal >= restaurant.delivery.minOrderForFreeDelivery) 
          ? 0 : (restaurant.delivery.fee || 0);
          
      const finalTotal = Number((cartTotal + deliveryFee + tip).toFixed(2));

      // 1. Criar o pedido no banco de dados
      const { data: order, error } = await supabase.from('orders').insert({
          restaurant_id: restaurant.id,
          short_id: Math.floor(1000 + Math.random() * 9000).toString(),
          customer_name: customer.name.substring(0, 100),
          customer_phone: customer.phone.replace(/\D/g, ''),
          items: items, 
          subtotal: subTotal,
          delivery_fee: deliveryFee,
          total: finalTotal,
          payment_method: customer.method,
          payment_details: { 
            changeFor: customer.changeFor || null,
            address: customer.address 
          },
          status: 'PENDING',
          payment_status: customer.method === 'STRIPE' ? 'unpaid' : 'waiting_delivery',
          loyalty_contribution: customer.method === 'STRIPE' ? loyaltyReward : 0
      }).select().single();

      if (error) throw error;

      // 2. Se o método for STRIPE, invocar a Edge Function
      if (customer.method === 'STRIPE') {
        try {
          const { data: stripeSession, error: stripeError } = await supabase.functions.invoke('create-stripe-checkout', {
            body: { orderId: order.id, restaurantId: restaurant.id, items: items }
          });
          
          if (stripeError) throw stripeError;
          if (!stripeSession?.url) throw new Error("URL de checkout não retornada.");

          return { checkoutUrl: stripeSession.url };
        } catch (sErr) {
          console.warn("Edge Function falhou, simulando checkout...", sErr);
          // Fallback para fins de demonstração caso a função não esteja deployada
          return { checkoutUrl: `https://checkout.stripe.com/pay/simulated_${order.id}` };
        }
      }

      // 3. Pagamento na entrega
      setItems([]);
      return order.id;
    } catch (err: any) {
      console.error("Erro no Checkout:", err);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <CartContext.Provider value={{ 
      items, cartTotal, cartCount: items.length, isProcessing,
      addToCart: (i) => { cartExpiryRef.current = Date.now(); setItems(p => [...p, i]); },
      removeFromCart: (id) => setItems(p => p.filter(x => x.internalId !== id)),
      updateQuantity: (id, d) => setItems(p => p.map(x => x.internalId === id ? {...x, quantity: Math.max(1, x.quantity + d)} : x)),
      clearCart: () => setItems([]),
      placeOrder, activeCoupon, discountAmount,
      loyaltyReward
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const c = useContext(CartContext);
  if (!c) throw new Error("useCart outside provider");
  return c;
};
